import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router, UrlTree } from '@angular/router';
import { first, Observable, ReplaySubject, shareReplay, tap } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';


import { Platform } from '@ionic/angular';
import { LoadingController, ModalController } from '@ionic/angular/standalone';

import { environment } from '../../environments';
import { AuthResult, AuthSubscriptionResult, User } from '../../models';
import { AlertService } from '../alert/alert.service';
import { BadgeService } from '../badge/badge.service';
import { CapacitiesService } from '../capacities/capacities.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { NotificationService } from '../notification/notification.service';
import { SignUpDataService } from '../sign-up-data/sign-up-data.service';
import { SocketIOService } from '../socket-io/socket-io.service';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private httpClient = inject(HttpClient);
  private loadingController = inject(LoadingController);
  private modalController = inject(ModalController);
  private platform = inject(Platform);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private alertService = inject(AlertService);
  private badgeService = inject(BadgeService);
  private capacitiesService = inject(CapacitiesService);
  private localStorageService = inject(LocalStorageService);
  private notificationService = inject(NotificationService);
  private signUpDataService = inject(SignUpDataService);
  private socketIOService = inject(SocketIOService);
  private userService = inject(UserService);

  protected authenticatedUser: ReplaySubject<User> = new ReplaySubject<User>(1);

  getAuthenticatedUser(): Observable<User> {
    return this.authenticatedUser;
  }

  subscription(email: string): Observable<AuthSubscriptionResult> {
    const params: HttpParams = new HttpParams().set('email', email);
    return this.httpClient.get<AuthSubscriptionResult>(`${environment.apiURL}/auth/subscription`, {
      params,
    });
  }

  login(email: string, password: string): Observable<AuthResult> {
    return this.httpClient
      .post<AuthResult>(`${environment.apiURL}/auth/login`, { email, password })
      .pipe(
        tap((result) => this.setSession(result)),
        shareReplay(),
      );
  }

  forgotPassword(email: string): Observable<void> {
    return this.httpClient.post<void>(`${environment.apiURL}/auth/password/forgot`, { email });
  }

  resetPassword(code: string, data: { email: string; password: string }): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.apiURL}/auth/password/forgot/${code}`,
      data,
    );
  }

  async isLoggedIn(urlTree = false): Promise<boolean | UrlTree> {
    const token = await this.localStorageService.get('token');
    return token ? true : urlTree ? this.router.parseUrl('/auth') : false;
  }

  setAuthenticatedUser(user: User): number {
    this.authenticatedUser.next(user);

    if (!user.confirmed) {
      this.signUpDataService.exists = true;
      this.signUpDataService.isSSO = !!user.singleSignOn;
      this.signUpDataService.email = user.email;
      this.signUpDataService.firstName = user.firstName;
      this.signUpDataService.lastName = user.lastName;
      this.signUpDataService.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (!this.signUpDataService.submitting) {
        this.router.navigate(['/auth/recognized']);
      }
      return -1;
    }

    if (user.flaggedForDeletion) {
      this.router.navigate(['reactivate-account']);
      return -1;
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (user.timezone !== timezone) {
      this.userService
        .updateCurrentUser({ timezone })
        .pipe(first())
        .subscribe({
          next: () => {
            this.authenticatedUser.next({ ...user, timezone });
            this.alertService.presentToast(
              this.translateService.instant('SETTINGS_LANGUAGE_TIMEZONE_UPDATED', { timezone }),
              'warning-toast',
              5000,
              'earth-outline',
            );
          },
        });
    }

    this.socketIOService.connect();
    this.setLanguage(user.language);

    if (this.platform.is('capacitor') && user.communication.mobilePushNotification) {
      this.notificationService.setMobilePushNotifications();
    } else if (this.platform.is('desktop') || this.platform.is('mobileweb')) {
      this.notificationService.setDesktopPushNotifications();
    }

    this.getAndSetUnreadTotalNotifications();

    return 0;
  }

  getAndSetUnreadTotalNotifications(): void {
    this.notificationService
      .getNotifications({ limit: 1, filter: { opened: false, archived: false } })
      .pipe(first())
      .subscribe(({ total }) => {
        this.notificationService.setUnreadTotal(total);

        if (this.platform.is('capacitor')) {
          this.badgeService.set(total);
        }
      });
  }

  setLanguage(lang: string): void {
    this.translateService.setDefaultLang(lang);
    this.translateService.use(lang);
  }

  private async setSession(authResult: AuthResult): Promise<void> {
    await this.localStorageService.set('token', authResult.token);
    this.setAuthenticatedUser(authResult.user);
  }

  async logout(): Promise<void> {
    const loading = await this.loadingController.create({
      message: this.translateService.instant('LOADING_LOGOUT_MESSAGE'),
    });

    loading.present();

    this.notificationService.removeDeviceRegistration().finally(() => {
      this.userService
        .disconnect()
        .pipe(first())
        .subscribe({
          complete: () => {
            loading.dismiss();
            this.disconnectLocally();
          },
        });
    });
  }

  disconnectLocally(): void {
    this.notificationService.setUnreadQuestInstance(0);
    this.notificationService.setUnreadTotal(0);

    if (this.platform.is('capacitor')) {
      this.badgeService.clear();
    }

    this.localStorageService.remove('token').then(() => {
      this.socketIOService.disconnect();
      this.router.navigate(['/auth'], { replaceUrl: true }).then(() => {
        if (this.capacitiesService.isMobile) {
          this.modalController.dismiss();
        }
      });
    });
  }
}
